import { useQuery, useMutation, useQueryClient } from "react-query";
import { adminMagazineAPI } from "./magazine.api";

export const queryKeys = {
  adminMagazines: (params) => ["admin", "magazines", params || {}],
  adminMagazineRoles: () => ["admin", "magazines", "roles"],
  adminMagazine: (id) => ["admin", "magazine", id ? String(id) : ""],
  adminMagazineLeads: (id, params) => ["admin", "magazine", id ? String(id) : "", "leads", params || {}],
  adminMagazineRequests: (id, params) => ["admin", "magazine", id ? String(id) : "", "requests", params || {}],
};

// ─── Admin Queries ────────────────────────────────────────────────────

export const useAdminMagazineRoles = () => {
  return useQuery(
    queryKeys.adminMagazineRoles(),
    () => adminMagazineAPI.getRoles(),
    {
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useAdminMagazines = (params = {}) => {
  return useQuery(
    queryKeys.adminMagazines(params),
    () => adminMagazineAPI.getAll(params),
    {
      staleTime: 30 * 1000,
      keepPreviousData: true,
    }
  );
};

export const useAdminMagazine = (id, options = {}) => {
  return useQuery(
    queryKeys.adminMagazine(id),
    () => adminMagazineAPI.getById(id),
    {
      enabled: Boolean(id),
      staleTime: 30 * 1000,
      ...options,
    }
  );
};

export const useAdminMagazineLeads = (id, params = {}) => {
  return useQuery(
    queryKeys.adminMagazineLeads(id, params),
    () => adminMagazineAPI.getLeads(id, params),
    {
      enabled: Boolean(id),
      staleTime: 30 * 1000,
    }
  );
};

export const useAdminMagazineRequests = (id, params = {}) => {
  return useQuery(
    queryKeys.adminMagazineRequests(id, params),
    () => adminMagazineAPI.getAccessRequests(id, params),
    {
      enabled: Boolean(id),
      staleTime: 30 * 1000,
    }
  );
};

// ─── Admin Mutations ──────────────────────────────────────────────────

export const useCreateMagazineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload) => adminMagazineAPI.create(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["admin", "magazines"]);
      },
    }
  );
};

export const useUpdateMagazineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload }) => adminMagazineAPI.update(id, payload),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["admin", "magazines"]);
        queryClient.invalidateQueries(queryKeys.adminMagazine(variables.id));
      },
    }
  );
};

export const useDeleteMagazineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id) => adminMagazineAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["admin", "magazines"]);
      },
    }
  );
};

export const useRegisterPagesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, pages }) => adminMagazineAPI.registerPages(id, pages),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(queryKeys.adminMagazine(variables.id));
        queryClient.invalidateQueries(["admin", "magazines"]);
      },
    }
  );
};

export const useReorderPagesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, pageOrders }) => adminMagazineAPI.reorderPages(id, pageOrders),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(queryKeys.adminMagazine(variables.id));
      },
    }
  );
};

export const useDeletePageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ magazineId, pageId }) => adminMagazineAPI.deletePage(magazineId, pageId),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(queryKeys.adminMagazine(variables.magazineId));
        queryClient.invalidateQueries(["admin", "magazines"]);
      },
    }
  );
};

export const useResolveAccessRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ magazineId, requestId, payload }) =>
      adminMagazineAPI.resolveAccessRequest(magazineId, requestId, payload),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([
          "admin",
          "magazine",
          String(variables.magazineId),
          "requests",
        ]);
      },
    }
  );
};

export const useImportPdfMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ formData, onProgress }) => adminMagazineAPI.importPdf(formData, onProgress),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["admin", "magazines"]);
        if (data?.id) {
          queryClient.invalidateQueries(["admin", "magazine", String(data.id)]);
        }
      },
    }
  );
};

